import React, { useEffect, useMemo, useState } from 'react';
import { useUserData } from '../../hooks/useUserData';
import { useSidebar } from '../../contexts/SidebarContext';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const EVENT_TYPES = [
  { value: 'lump_inflow', label: 'Lump-sum Inflow' },
  { value: 'lump_outflow', label: 'Lump-sum Outflow' },
  { value: 'asset_sale', label: 'Asset Sale' },
  { value: 'debt_prepayment', label: 'Debt Prepayment' }
];

const SpecialEvents = () => {
  const { userData, saveSpecialEvents, loading } = useUserData();
  const { sidebarExpanded, toggleSidebar } = useSidebar();

  const startYear = useMemo(() => Number(userData?.setup?.startYear) || new Date().getFullYear(), [userData]);
  const horizon = useMemo(() => Math.max(1, Math.min(60, Number(userData?.setup?.projectionHorizon) || 20)), [userData]);
  const yearOptions = useMemo(() => Array.from({ length: horizon + 1 }, (_, i) => startYear + i), [startYear, horizon]);

  const assetOptions = useMemo(() => (userData?.portfolio || []).map(a => ({ id: String(a.id), name: a.name || a.type || 'Asset' })), [userData]);
  const debtOptions = useMemo(() => (userData?.debts || []).map(d => ({ id: String(d.id), name: d.name || 'Loan' })), [userData]);

  const initialRows = useMemo(() => {
    const persisted = Array.isArray(userData?.specialEvents) ? userData.specialEvents : [];
    return persisted.map(e => ({
      id: e.id || `${Date.now()}-${Math.random()}`,
      type: e.type || 'lump_inflow',
      year: Number(e.year) || startYear,
      amount: Math.max(0, Number(e.amount) || 0),
      targetId: e.targetId || ''
    }));
  }, [userData, startYear]);

  const [rows, setRows] = useState(initialRows);

  useEffect(() => {
    setRows(initialRows);
  }, [initialRows]);

  const addRow = () => {
    setRows(prev => [
      ...prev,
      { id: `${Date.now()}-${Math.random()}`, type: 'lump_inflow', year: startYear, amount: 0, targetId: '' }
    ]);
  };

  const removeRow = (id) => {
    setRows(prev => prev.filter(r => r.id !== id));
  };

  const updateRow = (id, patch) => {
    setRows(prev => prev.map(r => r.id === id ? { ...r, ...patch } : r));
  };

  const requiresTarget = (type) => type === 'asset_sale' || type === 'debt_prepayment';

  const validateRow = (r) => {
    const typeOk = EVENT_TYPES.some(t => t.value === r.type);
    const yearOk = yearOptions.includes(Number(r.year));
    const amountOk = Number(r.amount) > 0;
    const targetOk = requiresTarget(r.type) ? Boolean(r.targetId) : true;
    return typeOk && yearOk && amountOk && targetOk;
  };

  const isValid = useMemo(() => rows.length > 0 && rows.every(validateRow), [rows]);

  const saveEvents = async () => {
    const payload = rows.map(r => ({ type: r.type, year: Number(r.year), amount: Number(r.amount), targetId: r.targetId || null }));
    await saveSpecialEvents(payload);
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />
      <Header 
        sidebarExpanded={sidebarExpanded}
        onSidebarToggle={toggleSidebar}
      />

      <main
        className={`
          transition-all duration-300 ease-spring pt-16 pb-20 lg:pb-8
          ${sidebarExpanded ? 'lg:ml-64' : 'lg:ml-16'}
        `}
      >
        <div className="p-4 sm:p-6 py-6 sm:py-8">
          <Breadcrumb />

          <div className="flex items-center justify-between mb-6 sm:mb-8 gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="CalendarPlus" size={16} className="text-primary" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-foreground">Special Events</h1>
                <p className="text-sm text-muted-foreground">Events run before surplus/deficit logic each year.</p>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
              <Button variant="outline" size="sm" onClick={addRow}>
                <Icon name="Plus" size={16} />
                Add Event
              </Button>
              <Button onClick={saveEvents} disabled={!isValid || loading}>
                <Icon name="Save" size={16} />
                Save Events
              </Button>
            </div>
          </div>

          <div className="bg-card rounded-lg border border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Event Type</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Year</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Amount</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Target (if applicable)</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {rows.map((r) => {
                    const showAsset = r.type === 'asset_sale';
                    const showDebt = r.type === 'debt_prepayment';
                    return (
                      <tr key={r.id} className="hover:bg-muted/30 transition-colors duration-200">
                        <td className="px-4 py-3">
                          <select
                            className="text-sm border border-border rounded px-2 py-1 bg-background"
                            value={r.type}
                            onChange={(e) => updateRow(r.id, { type: e.target.value, targetId: '' })}
                          >
                            {EVENT_TYPES.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-3">
                          <select
                            className="text-sm border border-border rounded px-2 py-1 bg-background"
                            value={r.year}
                            onChange={(e) => updateRow(r.id, { year: Number(e.target.value) })}
                          >
                            {yearOptions.map(y => (
                              <option key={y} value={y}>{y}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="number"
                            min={0}
                            step={1000}
                            className="w-36 text-sm border rounded px-2 py-1 bg-background"
                            value={r.amount}
                            onChange={(e) => updateRow(r.id, { amount: Math.max(0, Number(e.target.value) || 0) })}
                          />
                        </td>
                        <td className="px-4 py-3">
                          {showAsset && (
                            <select
                              className="text-sm border border-border rounded px-2 py-1 bg-background"
                              value={r.targetId}
                              onChange={(e) => updateRow(r.id, { targetId: e.target.value })}
                            >
                              <option value="">Select asset…</option>
                              {assetOptions.map(a => (
                                <option key={a.id} value={a.id}>{a.name}</option>
                              ))}
                            </select>
                          )}
                          {showDebt && (
                            <select
                              className="text-sm border border-border rounded px-2 py-1 bg-background"
                              value={r.targetId}
                              onChange={(e) => updateRow(r.id, { targetId: e.target.value })}
                            >
                              <option value="">Select loan…</option>
                              {debtOptions.map(d => (
                                <option key={d.id} value={d.id}>{d.name}</option>
                              ))}
                            </select>
                          )}
                          {!showAsset && !showDebt && (
                            <span className="text-xs text-muted-foreground">Not required</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeRow(r.id)}
                            className="text-muted-foreground hover:text-destructive"
                            title="Remove event"
                          >
                            <Icon name="Trash2" size={16} />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                  {rows.length === 0 && (
                    <tr>
                      <td className="px-4 py-6 text-sm text-muted-foreground" colSpan={5}>No events added yet</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SpecialEvents;


